import React, { useEffect } from "react";

import { useLazyQuery } from "@apollo/client";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Theme,
  Typography,
} from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useClientAllergies from "@/hooks/useClientAllergies";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { humanize } from "@/utils/string";

import CircularProgressBar from "./CircularProgressBar";
import GramsProgressItem from "./GramsProgressItem";
import AllergenFoodIcon from "../Icons/AllergenFoodIcon";

interface Props {
  foodId: string;
  fromAdded?: boolean;
  visibility: boolean;
  onAddIngredient: () => void;
  onClose: () => void;
  onRemoveIngredient?: () => void;
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    height: "100vh",
    width: "100%",
    display: "grid",
    placeItems: "center",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(8),
    width: 580,
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  name: {
    fontSize: 20,
    fontWeight: 700,
  },
  nameHeader: {
    marginTop: 20,
    display: "flex",
    alignItems: "center",
    gap: 13,
  },
  progresses: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: theme.spacing(4),
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: "#EDF0F5",
  },
  grams: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingLeft: 15,
    paddingRight: 25,
  },
  circularProgress: {
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderRightColor: "#EDF0F5",
    paddingTop: 15,
    paddingRight: 15,
  },
  warningView: {
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: colors.dark,
  },
  warningViewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: theme.spacing(2, 4),
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: colors.dark,
    backgroundColor: "#FF6262",
  },
  warningContent: {
    padding: theme.spacing(2, 4),
  },
  warningTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: colors.white,
  },
  warningDescription: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: 14,
  },
  activeAllergen: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
  },
  activeAllergenText: {
    fontWeight: 700,
    fontSize: 14,
  },
  addRecipe: {
    backgroundColor: "transparent",
    color: "#FF6262",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  warningIndicator: {
    marginLeft: 10,
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 12px 4px 12px",
    backgroundColor: "#FF6262",
    borderRadius: 24,
  },
  warningIndicatorText: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.white,
  },
}));

const allergenIngredientModalClientFragment = graphql(`
  fragment AllergenIngredientModal_Client on Client {
    id
    profile {
      id
      firstName
    }
  }
`);

const allergenIngredientModalQuery = graphql(`
  query AllergenIngredientModal($id: ID!, $clientAllergies: [AllergiesKind!]) {
    food(id: $id) {
      id
      name
      carbohydrateGrams
      fatGrams
      proteinGrams
      energyKcal
      epWeightGrams
      allergies(clientAllergies: $clientAllergies)
    }
  }
`);

const AllergenIngredientModal: React.FC<Props> = ({
  foodId,
  fromAdded,
  visibility,
  onAddIngredient,
  onRemoveIngredient,
  onClose,
}) => {
  const { classes } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const clientAllergies = useClientAllergies(clientId ?? "");
  const [client] = useFragment(
    allergenIngredientModalClientFragment,
    `Client:${clientId}`
  );
  const { state } = useLocation();
  const clientName = client
    ? client?.profile?.firstName
    : state?.clientFirstName;

  const [fetch, query] = useLazyQuery(allergenIngredientModalQuery, {
    variables: { id: foodId, clientAllergies: clientAllergies },
  });
  useEffect(() => {
    if (visibility) fetch();
  }, [visibility]);

  const food = query.data?.food;
  const foodAllergies = food?.allergies;

  function getMacroPercentage(macro: number) {
    const epWeightGrams = food?.epWeightGrams ?? 0;
    return Math.round((macro / epWeightGrams) * 100);
  }

  const carbPercentage = getMacroPercentage(food?.carbohydrateGrams ?? 0);
  const fatPercentage = getMacroPercentage(food?.fatGrams ?? 0);
  const proteinPercentage = getMacroPercentage(food?.proteinGrams ?? 0);

  return (
    <Modal open={visibility} onClose={onClose} className={classes.root}>
      <div className={classes.modalBody}>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Typography className={classes.modalHeaderText}>
            Allergen Alert
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="column" gap="30px">
          <div className={classes.nameHeader}>
            <AllergenFoodIcon />
            <Typography className={classes.name} variant="body1">
              {food?.name}
            </Typography>
          </div>
          <div className={classes.progresses}>
            <div className={classes.circularProgress}>
              <CircularProgressBar
                primaryColor="#DFE6EC"
                calories={`${food?.energyKcal ?? 0}`}
                data={[
                  {
                    value: carbPercentage,
                    color: "#F76C41",
                  },
                  {
                    value: fatPercentage,
                    color: "#2D87BB",
                  },
                  {
                    value: proteinPercentage,
                    color: colors.secondary,
                  },
                ]}
              />
            </div>
            <div className={classes.grams}>
              <GramsProgressItem
                title="Carbs"
                percentage={carbPercentage}
                grams={`${food?.carbohydrateGrams}`}
              />
              <GramsProgressItem
                title="Fat"
                percentage={fatPercentage}
                grams={`${food?.fatGrams}`}
              />
              <GramsProgressItem
                title="Protein"
                percentage={proteinPercentage}
                grams={`${food?.proteinGrams}`}
              />
            </div>
          </div>
          <div className={classes.warningView}>
            <div className={classes.warningViewHeader}>
              <WarningIcon htmlColor={colors.white} />
              <Typography className={classes.warningTitle} variant="body1">
                FOOD ALLERGEN WARNING
              </Typography>
            </div>
            <div className={classes.warningContent}>
              <Typography
                className={classes.warningDescription}
                variant="body2"
              >
                Oops! It seems like {clientName} and {food?.name} aren&apos;t
                the best of buddies due to an allergen. Trust me, it&apos;s like
                mixing oil and water—just not a match made in food heaven!
              </Typography>
              <div className={classes.activeAllergen}>
                <Typography
                  className={classes.activeAllergenText}
                  variant="body2"
                >
                  Active allergen:
                </Typography>
                <div className={classes.warningIndicator}>
                  {foodAllergies?.map((allergy, index) => (
                    <Typography
                      key={index}
                      className={classes.warningIndicatorText}
                    >
                      {index < foodAllergies.length - 1
                        ? `${humanize(allergy)}, `
                        : humanize(allergy)}
                    </Typography>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <Box display="flex" flexDirection="row" alignSelf="center" gap="20px">
            {fromAdded ? (
              <Button onClick={onRemoveIngredient}>Remove Ingredient</Button>
            ) : (
              <Button className={classes.addRecipe} onClick={onAddIngredient}>
                Add Ingredient
              </Button>
            )}
          </Box>
        </Box>
      </div>
    </Modal>
  );
};

export default AllergenIngredientModal;
